/**
 * Reading a job's media, for the screens that render it.
 *
 * Every selector here is written so that "there are none" is an ordinary answer rather
 * than a special case: photos are optional at every stage (D2, D33), so an empty array
 * and an empty group are the common shape, and each caller is expected to render nothing
 * at all rather than a placeholder for the photo that was never required.
 */

import {
  AttachmentKind,
  AttachmentStage,
  attachmentStageLabelKey,
  type TicketAttachment,
} from '@nbh/shared';
import { formatDateTime } from './format';

/**
 * Lifecycle order, which is deliberately not the order the array arrives in.
 *
 * The wire order is oldest-first by upload time, and an upload that limped in over 3G
 * hours after it was taken would otherwise file a fault photo after the sign-off shot.
 * Grouping by stage in the order the job actually moves is what makes the gallery read
 * as a story.
 */
const STAGE_ORDER: AttachmentStage[] = [
  AttachmentStage.Issue,
  AttachmentStage.Completion,
  AttachmentStage.Verification,
];

/**
 * The heading for media with no stage.
 *
 * A null stage is a real answer, not a gap: today it is a checklist-step photo, which
 * attests to one SOP step rather than to a transition. Folding it under a lifecycle
 * heading would file it as evidence of something it never claimed.
 */
export const CHECKLIST_EVIDENCE_LABEL_KEY = 'attachment.checklistEvidence';

export interface AttachmentGroup {
  /** The stage this group is, or null for the checklist-evidence group. */
  stage: AttachmentStage | null;
  labelKey: string;
  items: TicketAttachment[];
}

/** A voice note is media on the same ticket, but it is not an image. */
export function isPhoto(attachment: TicketAttachment): boolean {
  return attachment.kind === AttachmentKind.Photo;
}

/** Every photo taken at one stage, in the order the server sent them (oldest first). */
export function photosAtStage(
  attachments: TicketAttachment[],
  stage: AttachmentStage,
): TicketAttachment[] {
  return attachments.filter((attachment) => attachment.stage === stage && isPhoto(attachment));
}

/** The one photo a header or a comparison pane shows, or null when there is none. */
export function firstPhotoAtStage(
  attachments: TicketAttachment[],
  stage: AttachmentStage,
): TicketAttachment | null {
  return photosAtStage(attachments, stage)[0] ?? null;
}

/**
 * The gallery's groups, lifecycle stages first and checklist evidence last.
 *
 * Empty groups are dropped rather than rendered as headings with nothing under them —
 * a stage with no photo has nothing to say, and saying it anyway reads as a reproach.
 */
export function groupAttachments(attachments: TicketAttachment[]): AttachmentGroup[] {
  const groups: AttachmentGroup[] = STAGE_ORDER.map((stage) => ({
    stage,
    labelKey: attachmentStageLabelKey[stage],
    items: attachments.filter((attachment) => attachment.stage === stage),
  }));

  groups.push({
    stage: null,
    labelKey: CHECKLIST_EVIDENCE_LABEL_KEY,
    items: attachments.filter((attachment) => attachment.stage === null),
  });

  return groups.filter((group) => group.items.length > 0);
}

/** i18n key for the alt text of a photo, per stage. */
const ALT_KEY: Record<AttachmentStage, string> = {
  [AttachmentStage.Issue]: 'attachment.issueAlt',
  [AttachmentStage.Completion]: 'attachment.completionAlt',
  [AttachmentStage.Verification]: 'attachment.verificationAlt',
};

/**
 * What this photo is of, in words.
 *
 * Alt text, not an empty string: on a handset that fails to fetch the bytes, the stage
 * is the only thing left that says what the reader is missing.
 */
export function altKeyFor(stage: AttachmentStage | null): string {
  return stage ? ALT_KEY[stage] : 'attachment.photoAlt';
}

/**
 * Who took it and when, in property time.
 *
 * `capturedAt` in preference to `createdAt`: the phone captures in a plant room and
 * uploads when it finds signal, so the upload time can be hours out from the moment the
 * fault was actually seen. A null uploader is a hard-deleted account (D25), which reads
 * as a former staff member rather than as a blank.
 */
export function attachmentCaption(
  attachment: TicketAttachment,
  t: (key: string) => string,
): string {
  const who = attachment.uploadedByName ?? t('detail.formerStaff');
  return `${who} · ${formatDateTime(attachment.capturedAt ?? attachment.createdAt)}`;
}
